/**
 * ⏸️ Suspend Corporate Account Modal
 * Modal for suspending a corporate account with optional reason
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, Warning } from '@mui/icons-material';
import {
  corporateAccountsService,
  CorporateAccount,
} from '@/services/corporateAccountsService';
import toast from 'react-hot-toast';

interface SuspendCorporateAccountModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onSuspend: () => void;
}

export const SuspendCorporateAccountModal: React.FC<SuspendCorporateAccountModalProps> = ({
  open,
  account,
  onClose,
  onSuspend,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');

    // Validate reason length if provided
    if (reason && (reason.length < 10 || reason.length > 1000)) {
      setError('Reason must be between 10 and 1000 characters');
      return;
    }

    if (!account) return;

    setSubmitting(true);
    try {
      const response = await corporateAccountsService.suspendCorporateAccount({
        accountId: account.id,
        reason: reason || undefined,
      });

      if (response.success) {
        toast.success(`${account.companyName} has been suspended`);
        onSuspend();
        handleClose();
      } else {
        setError(response.error || 'Failed to suspend account');
      }
    } catch (error) {
      console.error('Failed to suspend account:', error);
      setError('Failed to suspend account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  if (!account) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Suspend Corporate Account</Typography>
          <IconButton onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Suspending this account will prevent them from making new bookings.
            Existing bookings will not be affected.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Account Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Company: <strong>{account.companyName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current Status: <strong>{account.status.toUpperCase()}</strong>
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Suspension Reason (Optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          error={!!error && error.includes('Reason')}
          helperText={
            error && error.includes('Reason')
              ? error
              : 'Optional: Provide a reason for suspending this account (10-1000 characters)'
          }
          placeholder="e.g., Payment delays, Terms of service violations, Investigation period..."
        />

        {error && !error.includes('Reason') && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Suspending...' : 'Suspend Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
